import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

const LocationHeader = React.memo(
  ({ loc, isOpen, onToggle, onRemove, canRemove }) => (
    <div
      className="flex justify-between items-center cursor-pointer bg-gray-50 px-4 py-2 border-b"
      onClick={onToggle}
    >
      <div className="text-sm font-medium">
        <span className="ml-2 text-gray-500 text-xs">
          {loc.short_code || 'NA'} {loc.city ? `• ${loc.city}` : ''}
        </span>
      </div>

      <div className="flex items-center gap-2">
        {canRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="text-red-500 text-xs"
          >
            Remove
          </button>
        )}
        <span className="text-lg">{isOpen ? '-' : '+'}</span>
      </div>
    </div>
  )
);

const LocationBody = React.memo(
  ({ loc, index, onChange }) => (
    <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
      <select
        name="delivery_type"
        value={loc.delivery_type || 'local'}
        onChange={(e) => onChange(index, e)}
        className="border rounded px-3 py-1.5 text-sm"
      >
        <option value="local">Local</option>
        <option value="nonlocal">Nonlocal</option>
        <option value="local_45_kms">Local &gt; 45KMS</option>
      </select>
      {[
        'short_code',
        'address_line1',
        'address_line2',
        'city',
        'state',
        'pincode'
      ].map(field => (
        <input
          key={field}
          name={field}
          value={loc[field] || ''}
          placeholder={field.replace('_', ' ')}
          onChange={(e) => onChange(index, e)}
          className="border rounded px-3 py-1.5 text-sm"
        />
      ))}
    </div>
  )
);

const ClientForm = ({ client, customers = [], dataErrors, onSave, onCancel }) => {

  const createEmptyLocation = () => ({
    _ui_id: crypto.randomUUID(),
    short_code: '',
    delivery_type: 'local',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    pincode: ''
  });

  const [formData, setFormData] = useState(() => ({
    id: client?.id || 0,
    client_code: client?.client_code || '',
    name: client?.name || '',
    phone: client?.phone || '',
    email: client?.email || '',
    customers: (client?.customers || []).map((id) => Number(id)),
    is_active: client?.is_active ? 1 : 0,
    locations: client?.locations?.length
    ? client.locations.map(loc => ({
        ...loc,
        delivery_type: loc?.delivery_type || 'local',
        _ui_id: crypto.randomUUID()
      }))
    : [createEmptyLocation()]
  }));
  const [validationErrors, setValidationErrors] = useState({});
  const [openIndex, setOpenIndex] = useState(0);

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (name === 'pincode') {
      value = value.replace(/\D/g, '').slice(0, 6);
    } else if (['name', 'email'].includes(name)) {
      value = value.toUpperCase();
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleLocationChange = (index, e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'pincode') {
      value = value.replace(/\D/g, '').slice(0, 6);
    } else if ([
      'short_code',
      'address_line1',
      'address_line2',
      'city',
      'state',
    ].includes(name)) {
      value = value.toUpperCase();
    }
    const locations = [...formData.locations];
    locations[index][name] = value;
    setFormData(prev => ({ ...prev, locations }));
  };

  const handleCustomerSelect = (e) => {
    const selected = Number(e.target.value);
    if (selected && !formData.customers.includes(selected)) {
      setFormData((prev) => ({
        ...prev,
        customers: [...prev.customers, selected],
      }));
    }
  };

  const handleCustomerRemove = (customerId) => {
    setFormData((prev) => ({
      ...prev,
      customers: prev.customers.filter((id) => id !== customerId),
    }));
  };

  const addLocation = () => {
    setFormData(prev => ({
      ...prev,
      locations: [...prev.locations, createEmptyLocation()]
    }));
  };

  const removeLocation = (index) => {
    if (formData.locations.length === 1) return;
    const locations = formData.locations.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, locations }));
  };

  const toggleAccordion = (index) => {
    setOpenIndex(prev => (prev === index ? null : index));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Name is required';
    // if (!/^\d{10}$/.test(formData.phone)) newErrors.phone = 'Invalid phone number';
    // if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) newErrors.email = 'Invalid email';
    formData.locations.forEach((loc, idx) => {
      // if (!loc.city) newErrors[`city_${idx}`] = 'City is required';
      if (loc.pincode && !/^\d{6}$/.test(loc.pincode)) {
        newErrors[`pincode_${idx}`] = 'Invalid pincode';
      }
    });
    return newErrors;
  };

  const cleanupData = (data) => {
    const client = { ...data };
    if (client.id == 0 &&  'client_code' in client) {
      delete client.client_code;
    }
    client.locations = client.locations.map(loc => ({
      ...loc,
      short_code: loc.short_code?.trim() || null
    }));
    client.customers = (client.customers || [])
      .map((id) => parseInt(id, 10))
      .filter(Number.isInteger);
    return client;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setValidationErrors({});
    const errors = validate();
    if (Object.keys(errors).length) {
      setValidationErrors(errors);
      return;
    }
    const client = cleanupData(formData);
    onSave(client);
  };

  // const LocationHeader = ({ loc, index, isOpen, onToggle, onRemove, canRemove }) => (
  //   <div
  //     className="flex justify-between items-center cursor-pointer bg-gray-50 px-4 py-2 border-b"
  //     onClick={onToggle}
  //   >
  //     <div className="text-sm font-medium">
  //       {/* Location {index + 1} */}
  //       <span className="ml-2 text-gray-500 text-xs">
  //         {loc.short_code || 'NA'} {loc.city ? `• ${loc.city}` : ''}
  //       </span>
  //     </div>

  //     <div className="flex items-center gap-2">
  //       {canRemove && (
  //         <button
  //           type="button"
  //           onClick={(e) => {
  //             e.stopPropagation();
  //             onRemove();
  //           }}
  //           className="text-red-500 text-xs"
  //         >
  //           Remove
  //         </button>
  //       )}
  //       <span className="text-lg">
  //         {isOpen ? '-' : '+'}
  //       </span>
  //     </div>
  //   </div>
  // );

  // const LocationBody = ({ loc, index, onChange }) => (
  //   <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
  //     <input
  //       name="short_code"
  //       placeholder="Short Code"
  //       value={loc.short_code}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />

  //     <input
  //       name="address_line1"
  //       placeholder="Address Line 1"
  //       value={loc.address_line1}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />

  //     <input
  //       name="address_line2"
  //       placeholder="Address Line 2"
  //       value={loc.address_line2}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />

  //     <input
  //       name="city"
  //       placeholder="City"
  //       value={loc.city}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />

  //     <input
  //       name="state"
  //       placeholder="State"
  //       value={loc.state}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />

  //     <input
  //       name="pincode"
  //       placeholder="Pincode"
  //       value={loc.pincode}
  //       onChange={(e) => onChange(index, e)}
  //       className="border rounded px-3 py-1.5 text-sm"
  //     />
  //   </div>
  // );

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded shadow p-4 space-y-4 [&_input[type='text']]:uppercase [&_textarea]:uppercase [&_select]:uppercase"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium mb-1">Name</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            required
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
        </div>
        {dataErrors.name && (
          <p className="text-red-500 text-sm mt-1">{dataErrors.name[0]}</p>
        )}
        {/* Contact */}
        <div>
          <label className="block text-sm font-medium mb-1">Contact</label>
          <input
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.phone && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.phone[0]}</p>
          )}
          {validationErrors.phone && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.phone}</p>
          )}
        </div>

        {/* Email */}
        <div>
          <label className="block text-sm font-medium mb-1">Email</label>
          <input
            name="email"
            value={formData.email}
            onChange={handleChange}
            type="email"
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.email && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.email[0]}</p>
          )}
          {validationErrors.email && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.email}</p>
          )}
        </div>

      </div>

      <div className="uppercase">
        <label className="block text-sm font-medium">Customers</label>
        <div className="border rounded px-3 py-2 w-full min-h-[40px] flex flex-wrap items-center gap-2">
          {(formData.customers || []).map((customerId) => {
            const selectedCustomer = customers.find((c) => Number(c.id) === Number(customerId));
            return (
              <span key={customerId} className="bg-blue-100 text-blue-700 text-xs rounded px-2 py-1 flex items-center uppercase">
                {selectedCustomer?.name || customerId}
                <XMarkIcon
                  className="h-4 w-4 ml-1 cursor-pointer"
                  onClick={() => handleCustomerRemove(customerId)}
                />
              </span>
            );
          })}
          <select
            onChange={handleCustomerSelect}
            className="bg-transparent text-sm text-gray-600 focus:outline-none"
            value={0}
          >
            <option value={0} disabled>SELECT CUSTOMER</option>
            {customers
              .filter((c) => !(formData.customers || []).includes(c.id))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        {dataErrors.customers && (
          <p className="text-red-500 text-sm mt-1">{dataErrors.customers[0]}</p>
        )}
      </div>

      {/* Address Section
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Short Code</label>
          <input
            name="short_code"
            value={formData.short_code}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.short_code && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.short_code[0]}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Address Line 1</label>
          <input
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.address_line1 && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.address_line1[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Address Line 2</label>
          <input
            name="address_line2"
            value={formData.address_line2}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.address_line2 && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.address_line2[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">City</label>
          <input
            name="city"
            value={formData.city}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.city && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.city[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">State</label>
          <input
            name="state"
            value={formData.state}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.state && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.state[0]}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Pincode</label>
          <input
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            className="border rounded w-full px-3 py-1.5 text-sm"
          />
          {dataErrors.pincode && (
              <p className="text-red-500 text-sm mt-1">{dataErrors.pincode[0]}</p>
          )}
          {validationErrors.pincode && (
            <p className="text-red-500 text-sm mt-1">{validationErrors.pincode}</p>
          )}
        </div>
      </div> */}
      <div className="space-y-3">
        <label className="block text-sm font-medium mb-1">Locations</label>
        {formData.locations.map((loc, index) => (
          <div key={loc._ui_id} className="border rounded overflow-hidden">
            <LocationHeader
              loc={loc}
              index={index}
              isOpen={openIndex === index}
              onToggle={() => toggleAccordion(index)}
              onRemove={() => removeLocation(index)}
              canRemove={formData.locations.length > 1}
            />

            {openIndex === index && (
              <LocationBody
                loc={loc}
                index={index}
                onChange={handleLocationChange}
              />
            )}
          </div>
        ))}
      </div>
      

      <button
        type="button"
        onClick={() => {
          addLocation();
          setOpenIndex(formData.locations.length); // Open the newly added location
        }}
        className="text-blue-600 text-sm"
      >
        + Add Location
      </button>


      {/* Buttons */}
      <div className="flex justify-end gap-2 mt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-1.5 border rounded text-sm hover:bg-gray-100"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="bg-blue-600 text-white px-4 py-1.5 rounded hover:bg-blue-700 text-sm"
        >
          Save
        </button>
      </div>
    </form>
  );
};

export default ClientForm;
